/**
 * OnboardingSpotlight
 *
 * Renders a non-interactive overlay with a "hole" punched around the element
 * currently set as `highlightTarget` in the onboarding store. The target can be
 * any CSS selector — by convention we use `[data-onboarding="<id>"]`
 * attributes which are easy to grep for and don't conflict with class refactors.
 *
 * The component:
 *   - Uses ResizeObserver + scroll/resize listeners to keep the spotlight
 *     aligned as the page changes (layout shifts, sticky headers, etc.).
 *   - Renders a soft glowing ring + animated pulse — visual cue, no blocking.
 *   - pointer-events: none so the user can still interact with the highlighted
 *     element. (We're guiding them TO it, not gating them.)
 *   - Auto-clears the highlight if the target element is removed from the DOM.
 */

import { useEffect, useLayoutEffect, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { useOnboardingStore } from '@/stores/onboardingStore';

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 8; // px breathing room around the highlighted element

function readRect(el: Element): Rect {
  const r = el.getBoundingClientRect();
  return {
    top: r.top - PADDING,
    left: r.left - PADDING,
    width: r.width + PADDING * 2,
    height: r.height + PADDING * 2,
  };
}

export function OnboardingSpotlight(): ReactElement | null {
  const target = useOnboardingStore((s) => s.highlightTarget);
  const setHighlight = useOnboardingStore((s) => s.setHighlight);

  const [rect, setRect] = useState<Rect | null>(null);
  const [element, setElement] = useState<Element | null>(null);

  // Resolve the selector → element. Re-run when target changes.
  useLayoutEffect(() => {
    if (!target) {
      setElement(null);
      setRect(null);
      return;
    }
    let cancelled = false;
    // Try immediately, then poll briefly if not found (target page may still be
    // mounting). Bail out after 1s to avoid leaking a forgotten poll loop.
    const start = Date.now();
    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(target);
      if (el) {
        setElement(el);
        setRect(readRect(el));
        return;
      }
      if (Date.now() - start < 1000) {
        requestAnimationFrame(tick);
      } else {
        // Target never appeared — clear the highlight so we don't render
        // an empty hover effect.
        setHighlight(null);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [target, setHighlight]);

  // Re-measure on scroll / resize / element mutation.
  useEffect(() => {
    if (!element) return;
    const update = () => setRect(readRect(element));
    update();

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(element);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    // If the element is removed from the DOM, clear the highlight.
    const mutationObserver = new MutationObserver(() => {
      if (!document.body.contains(element)) {
        setHighlight(null);
      }
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      mutationObserver.disconnect();
    };
  }, [element, setHighlight]);

  if (!rect || !target) return null;

  return createPortal(
    <div
      className="pointer-events-none fixed inset-0 z-[55]"
      data-testid="onboarding-spotlight"
    >
      {/* Glow ring around the target */}
      <div
        className="absolute rounded-xl ring-4 ring-blue-500/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)] transition-all duration-200"
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        }}
      />
      {/* Pulse ring for extra attention */}
      <div
        className="absolute rounded-xl ring-2 ring-blue-400 animate-ping"
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        }}
      />
    </div>,
    document.body
  );
}

export default OnboardingSpotlight;
